import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/formatters';
import { executeOrder } from '../services/trading';
import { API_BASE_URL } from '../config';
const OrderBookComponent = ({
  stockId,
  ticker,
  currentPrice,
  userId,
  onPositionUpdate
}) => {
  const navigate = useNavigate();
  const [recentTrades, setRecentTrades] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pressure, setPressure] = useState({ buy: 50, sell: 50, momentum: 'neutral' });
  const [orderForm, setOrderForm] = useState({
    type: 'LONG', // LONG or SHORT
    quantity: '1',
    price: currentPrice || 0
  });
  const [placingOrder, setPlacingOrder] = useState(false);

  // Fetch real trade data and calculate market pressure
  const fetchTradeData = async () => {
    if (!stockId) return;

    setLoading(true);
    try {
      // Fetch stock details (includes open positions)
      const stockResponse = await fetch(`${API_BASE_URL}/api/stocks/${stockId}`);
      let openPositions = [];

      if (stockResponse.ok) {
        const stockData = await stockResponse.json();
        openPositions = stockData.openPositions || [];
        setAllPositions(stockData.allPositions || []);
      }

      // Fetch recent trades for this stock (using public endpoint)
      const tradesResponse = await fetch(
        `${API_BASE_URL}/api/stocks/${stockId}/trades?limit=50`
      );

      let tradeBuyVolume = 0;
      let tradeSellVolume = 0;

      if (tradesResponse.ok) {
        const trades = await tradesResponse.json();
        setRecentTrades(trades.slice(0, 20)); // Keep last 20 trades

        // Calculate buy/sell pressure from recent trades
        const recentForPressure = trades.slice(0, 30);

        recentForPressure.forEach(trade => {
          const volume = trade.shares || 1;
          if (trade.type === 'LONG' || trade.type === 'BUY' || trade.type === 'CLOSE_SHORT') {
            tradeBuyVolume += volume;
          } else if (trade.type === 'SHORT' || trade.type === 'SELL' || trade.type === 'CLOSE_LONG') {
            tradeSellVolume += volume;
          }
        });
      }

      // Calculate position-based pressure (current market sentiment)
      let positionLongShares = 0;
      let positionShortShares = 0;

      openPositions.forEach(pos => {
        if (pos.type === 'LONG') {
          positionLongShares += pos.totalShares || pos.shares || 0;
        } else if (pos.type === 'SHORT') {
          positionShortShares += pos.totalShares || pos.shares || 0;
        }
      });

      // Combine trade activity (30%) with current positions (70%)
      // Positions reflect actual market sentiment
      const totalTradeVolume = tradeBuyVolume + tradeSellVolume;
      const totalPositionShares = positionLongShares + positionShortShares;

      let buyPercent = 50;
      let sellPercent = 50;

      if (totalPositionShares > 0 || totalTradeVolume > 0) {
        // Weight: 70% positions, 30% recent trades
        const positionWeight = 0.7;
        const tradeWeight = 0.3;

        const positionBuyRatio = totalPositionShares > 0
          ? positionLongShares / totalPositionShares
          : 0.5;
        const tradeBuyRatio = totalTradeVolume > 0
          ? tradeBuyVolume / totalTradeVolume
          : 0.5;

        // If only one has data, use it fully
        if (totalPositionShares > 0 && totalTradeVolume === 0) {
          buyPercent = Math.round(positionBuyRatio * 100);
        } else if (totalTradeVolume > 0 && totalPositionShares === 0) {
          buyPercent = Math.round(tradeBuyRatio * 100);
        } else {
          buyPercent = Math.round((positionBuyRatio * positionWeight + tradeBuyRatio * tradeWeight) * 100);
        }

        sellPercent = 100 - buyPercent;
      }

      const momentum = buyPercent > 55 ? 'bullish' : sellPercent > 55 ? 'bearish' : 'neutral';
      setPressure({ buy: buyPercent, sell: sellPercent, momentum });

    } catch (error) {
      console.error('Error fetching trade data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize trade data
  useEffect(() => {
    fetchTradeData();

    // Refresh trade data every 30 seconds
    const interval = setInterval(fetchTradeData, 30000);
    return () => clearInterval(interval);
  }, [stockId, currentPrice]);

  // Update order form price when current price changes
  useEffect(() => {
    setOrderForm(prev => ({
      ...prev,
      price: currentPrice || prev.price
    }));
  }, [currentPrice]);

  // Handle order form changes
  const handleOrderFormChange = (field, value) => {
    setOrderForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle order placement
  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    if (!userId) {
      if (onPositionUpdate) {
        onPositionUpdate({ error: 'Please log in to place orders' }, { actionType: 'ORDER', stockTicker: ticker });
      }
      return;
    }

    const qty = Math.max(1, parseInt(orderForm.quantity) || 1);
    if (qty <= 0) {
      if (onPositionUpdate) {
        onPositionUpdate({ error: 'Quantity must be greater than 0' }, { actionType: 'ORDER', stockTicker: ticker });
      }
      return;
    }

    setPlacingOrder(true);
    try {
      // Use the existing trading service
      const result = await executeOrder(userId, stockId, orderForm.type, qty);

      if (onPositionUpdate) {
        onPositionUpdate(result, {
          actionType: `OPEN_${orderForm.type}`,
          stockTicker: ticker,
          tradeAmount: orderForm.quantity,
          entryPrice: result.position?.entryPrice || orderForm.price
        });
      }

      // Reset form
      setOrderForm({
        type: 'LONG',
        quantity: '1',
        price: currentPrice
      });

      // Refresh trade data
      setTimeout(fetchTradeData, 1000);

    } catch (error) {
      console.error('Error placing order:', error);
      if (onPositionUpdate) {
        onPositionUpdate({ error: error.message }, {
          actionType: `OPEN_${orderForm.type}`,
          stockTicker: ticker,
          tradeAmount: orderForm.quantity
        });
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  return (
    <div className="orderbook-component">
      <div className="orderbook-header">
        <h3>Trading - {ticker}</h3>
        <div className="current-price-display">
          <span className="label">Current Price:</span>
          <span className="price">{formatCurrency(currentPrice)}</span>
        </div>
      </div>

      {/* Buy/Sell Pressure Meter */}
      <div className="pressure-meter-section">
        <div className="pressure-labels">
          <span className="pressure-label buy">BUYERS</span>
          <span className={`pressure-momentum ${pressure.momentum}`}>
            {pressure.momentum === 'bullish' ? '📈 Bullish' :
             pressure.momentum === 'bearish' ? '📉 Bearish' : '➡️ Neutral'}
          </span>
          <span className="pressure-label sell">SELLERS</span>
        </div>
        <div className="pressure-meter">
          <div
            className="pressure-bar buy"
            style={{ width: `${pressure.buy}%` }}
          >
            {pressure.buy > 15 && <span className="pressure-percent">{pressure.buy}%</span>}
          </div>
          <div
            className="pressure-bar sell"
            style={{ width: `${pressure.sell}%` }}
          >
            {pressure.sell > 15 && <span className="pressure-percent">{pressure.sell}%</span>}
          </div>
        </div>
        <div className="pressure-description">
          Based on open market positions
        </div>
      </div>

      <div className="orderbook-content">
        {/* Order Form */}
        <div className="order-form-section">
          <h4>Place Order</h4>
          <form onSubmit={handlePlaceOrder} className="order-form">
            <div className="form-row">
              <div className="order-type-selector">
                <button
                  type="button"
                  className={`type-btn ${orderForm.type === 'LONG' ? 'active' : ''} long`}
                  onClick={() => handleOrderFormChange('type', 'LONG')}
                >
                  Long (Buy)
                </button>
                <button
                  type="button"
                  className={`type-btn ${orderForm.type === 'SHORT' ? 'active' : ''} short`}
                  onClick={() => handleOrderFormChange('type', 'SHORT')}
                >
                  Short (Sell)
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="input-group">
                <label>Quantity</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={orderForm.quantity}
                  onChange={(e) => handleOrderFormChange('quantity', e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={() => setOrderForm(prev => ({ ...prev, quantity: String(Math.max(1, Math.min(1000, parseInt(prev.quantity) || 1))) }))}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="input-group">
                <label>Price (Market)</label>
                <input
                  type="number"
                  step="0.01"
                  value={orderForm.price.toFixed(2)}
                  readOnly
                  className="readonly"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="order-summary">
                <span className="label">Total:</span>
                <span className="total">
                  {formatCurrency((parseInt(orderForm.quantity) || 0) * orderForm.price)}
                </span>
              </div>
            </div>

            <button
              type="submit"
              className={`place-order-btn ${orderForm.type.toLowerCase()}`}
              disabled={placingOrder || !userId}
            >
              {placingOrder ? 'Placing Order...' : `Place ${orderForm.type} Order`}
            </button>
          </form>
        </div>

        {/* Open Positions */}
        <div className="open-positions-section">
          <h4>Open Positions ({allPositions.length})</h4>
          <div className="positions-list">
            <div className="positions-header">
              <span>User</span>
              <span>Type</span>
              <span>Shares</span>
              <span>Entry</span>
            </div>
            <div className="positions-rows">
              {allPositions.length === 0 ? (
                <div className="no-positions">No open positions</div>
              ) : (
                allPositions.map((pos, index) => (
                  <div key={pos.id || index} className={`position-row ${pos.type.toLowerCase()}`}>
                    <span
                      className="username clickable"
                      onClick={() => navigate(`/portfolio/${pos.userId}`)}
                    >
                      {pos.username}
                    </span>
                    <span className="type">{pos.type}</span>
                    <span className="shares">{pos.shares}</span>
                    <span className="entry-price">{formatCurrency(pos.entryPrice)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Trades */}
        <div className="recent-trades-section">
          <h4>Recent Trades</h4>
          <div className="trades-list">
            <div className="trades-header">
              <span>Time</span>
              <span>Type</span>
              <span>Price</span>
              <span>Quantity</span>
            </div>
            <div className="trades-rows">
              {recentTrades.length === 0 ? (
                <div className="no-trades">No recent trades available</div>
              ) : (
                recentTrades.map((trade, index) => (
                  <div key={index} className={`trade-row ${trade.type.toLowerCase()}`}>
                    <span className="time">
                      {new Date(trade.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </span>
                    <span className="type">{trade.type}</span>
                    <span className="price">{formatCurrency(trade.price)}</span>
                    <span className="quantity">{trade.shares}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="orderbook-controls">
        <button
          onClick={fetchTradeData}
          className="refresh-button"
          disabled={loading}
        >
          {loading ? '⟳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>
    </div>
  );
};

export default OrderBookComponent;